import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { capitalize } from "lodash";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import { GlobalTableHeightWrapper } from "../../../../../../../components/GlobalTableHeightWrapper";
import RenderWithTooltip from "../../../../../../../components/RenderWithTooltip";
import Text from "../../../../../../../components/Text";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import CreateClaimDrawer from "../../../../billings/claims/CreateClaimDrawer";
import { useRoleController } from "../../../../../../../components/useRoleController";
import { NEW_APPOINTMENT_VISIT_TYPE_MAPPER } from "../../../../../../../constants/scheduling";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useAppointmentsV2 } from "../../../../../../../queries/patient-portal/forms/appointment/use-appointment-list-v2";
import { formatDateTime } from "../../../../../../../utils/date-time-utility";
import {
  applyDefaultColumnSettings,
  DEF_PAGINATION,
  DGT_Pagination,
  filterActionCol,
  getXtenantId,
  roleFilter,
} from "../../../../../../../utils/utils";
import ViewEncounterSummaryDrawer from "../../../../billings/encounter/ViewEncounterSummaryDrawer";

const PatientReadyForBilling = () => {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const viewEncounterDisclosure = useDisclosure();
  const addClaimsDisclosure = useDisclosure();

  const { hasReadOnly } = useRoleController("Financials");
  const { hasView, hasReadOnly: hasReadOnlyEncounterBilling } =
    useRoleController("Encounter for billing");
  const { hasUpdate: hasUpdateSuperbill } =
    useRoleController("Generate Superbill");
  const { hasUpdate: hasUpdateClaims } = useRoleController("Generate Claims");

  const {
    data: appointmentsList,
    isLoading,
    isFetching,
  } = useAppointmentsV2({
    pagination,
    gridSorting,
    providerGroupId: getXtenantId(),
    appointmentStatus: ["COMPLETED"],
    // superBillGenerated: false,
    patientUuid: patientId ? [patientId] : undefined,
  });

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        field: "uuid",
        flex: 0.7,
        minWidth: 120,
        headerName: "Visit ID",
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Text isTabletext>{row?.uuid?.split("-")?.[0]?.toUpperCase()}</Text>
          );
        },
      },
      {
        field: "appointmentDate",
        flex: 1,
        minWidth: 170,
        headerName: "Visit Date & Time",
        sortable: false,
        renderCell: ({ row }) => formatDateTime(row?.startTime) || "-",
      },
      {
        field: "appointmentMode",
        flex: 0.6,
        minWidth: 160,
        headerName: "Appointment Mode",
        sortable: false,
        renderCell: ({ row }) =>
          row?.mode ? NEW_APPOINTMENT_VISIT_TYPE_MAPPER[row?.mode as any] : "-",
      },
      {
        field: "providerName",
        flex: 1,
        minWidth: 150,
        headerName: "Provider",
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Box>
              <Text isTabletext sx={{ lineHeight: 1.2 }}>
                {row?.providerName ?? "-"}
              </Text>
              <Text isTabletext c={"gray"} sx={{ lineHeight: 1.2 }}>
                {row?.providerRole ?? ""}
              </Text>
            </Box>
          );
        },
      },
      {
        field: "type",
        flex: 1,
        minWidth: 150,
        headerName: "Appointment Type",
        sortable: false,
        renderCell: ({ row }) =>
          row?.type ? capitalize(row?.type.replace(/_/g, " ")) : "-",
      },
      {
        field: "chiefComplaint",
        flex: 1.2,
        minWidth: 150,
        headerName: "Reason for Visit",
        sortable: false,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "locationName",
        flex: 0.8,
        minWidth: 150,
        headerName: "Place of Visit",
        sortable: false,
        renderCell: ({ value }) => value || "-",
      },
      {
        field: "Action",
        width: 90,
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={roleFilter({
                list: [
                  { value: "view", label: "View Visit" },
                  { value: "createSuperBill", label: "Create Invoice" },
                  { value: "createClaim", label: "Create Claim" },
                ],
                compWith: {
                  view: hasView,
                  createSuperBill: hasUpdateSuperbill,
                  createClaim: hasUpdateClaims,
                },
                removeOtherFields: hasReadOnlyEncounterBilling,
              })}
              onActionClick={(v) => {
                if (v === "view") {
                  if (viewEncounterDisclosure.data === row.uuid) {
                    viewEncounterDisclosure.close();
                    setTimeout(() => {
                      viewEncounterDisclosure.open(row.uuid);
                    }, 0);
                  } else {
                    viewEncounterDisclosure.open(row.uuid);
                  }
                } else if (v === "createSuperBill") {
                  navigate(
                    row?.uuid
                      ? "/app/provider/billing/invoice/" +
                          row?.patientId +
                          "/" +
                          row?.uuid
                      : "1"
                  );
                } else if (v === "createClaim") {
                  addClaimsDisclosure.open(row);
                }
              }}
            />
          );
        },
      },
    ],
    ["appointmentDate"]
  );

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ViewEncounterSummaryDrawer disclosure={viewEncounterDisclosure} />

      <CreateClaimDrawer
        disClosureHost={addClaimsDisclosure}
        title="Create Claim"
      />

      <GlobalTableHeightWrapper marginTop={0}>
        <CustomDataGridV2
          getRowId={(row) => row?.uuid}
          rows={appointmentsList?.data?.content ?? []}
          columns={filterActionCol(COLUMNS, hasReadOnly)}
          rowCount={appointmentsList?.data?.page?.totalElements ?? 10}
          disableRowSelectionOnClick
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          loading={isLoading || isFetching}
        />
      </GlobalTableHeightWrapper>
    </Box>
  );
};

export default PatientReadyForBilling;
